/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import {
	IMAGE_CONFIG,
	isPlatformBrowser,
	registerLocaleData,
} from "@angular/common";
import {
	HttpClient,
	HttpHeaders,
	provideHttpClient,
	withFetch,
	withInterceptorsFromDi,
} from "@angular/common/http";
import localeIt from "@angular/common/locales/it";
import {
	type ApplicationConfig,
	ErrorHandler,
	Injectable,
	Injector,
	LOCALE_ID,
	PLATFORM_ID,
	importProvidersFrom,
	inject,
	provideAppInitializer,
} from "@angular/core";
import {
	provideClientHydration,
	withHttpTransferCacheOptions,
} from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import {
	Router,
	provideRouter,
	withComponentInputBinding,
	withRouterConfig,
} from "@angular/router";
import { DndModule } from "@ng-dnd/core";
import { DndSortableModule } from "@ng-dnd/sortable";
import {
	MissingTranslationHandler,
	type MissingTranslationHandlerParams,
	TranslateLoader,
	TranslateModule,
} from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import * as Sentry from "@sentry/angular";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Subject } from "rxjs";
import { environment } from "src/environments/environment";
import { CbRoutesUtils } from "./@classes/routes/cb-routes-utils";
import { SupabaseInitService } from "./@classes/supabase";

import player from "lottie-web";
import {
	provideCacheableAnimationLoader,
	provideLottieOptions,
} from "ngx-lottie";

registerLocaleData(localeIt, "it-IT");

function getBrowserLocale() {
	let language = "it";

	try {
		language = navigator.language;
	} catch (error) {
		//console.error(error);
	}

	try {
		const langS = localStorage.getItem("lang");
		if (langS) {
			language = langS;
		}
	} catch (error) {
		//console.error(error);
	}

	if (language.startsWith("it")) {
		return "it-IT";
	}

	return "it-IT";
}

function getDefaultLanguage() {
	let defaultLang = "it";
	let browserLang = "it";

	try {
		browserLang = navigator.language;
	} catch (error) {
		//console.error(error);
	}

	try {
		const langS = localStorage.getItem("lang");
		if (langS) {
			browserLang = langS;
		}
	} catch (error) { }

	if (browserLang.startsWith("it")) {
		defaultLang = "it";
	}

	return defaultLang;
}
@Injectable()
export class MyMissingTranslationHandler implements MissingTranslationHandler {
	private readonly injector: Injector = inject(Injector);
	private platformId = inject(PLATFORM_ID);
	private http = inject(HttpClient);
	// Tracking set to avoid duplicate requests
	private processedKeys = new Set<string>();
	private translationUpdated = new Subject<string>(); // Subject to notify about updated translations
	private ignoreKeys = new Set<string>();

	constructor() {
		this.translationUpdated.subscribe((key) => {
			this.updateTranslation(key);
		});
	}

	handle(params: MissingTranslationHandlerParams): string {
		const key = params.key;
		if (this.ignoreKeys.has(key)) {
			return key;
		}
		if (!environment.production && isPlatformBrowser(this.platformId)) {
			if (!this.processedKeys.has(key)) {
				this.processedKeys.add(key);
				// Make the API call without waiting for it
				this.translationUpdated.next(key);
				this.ignoreKeys.add(key);
			}
		}
		return key; // Return the key immediately
	}

	private updateTranslation(key: string): void {
		if (this.ignoreKeys.has(key)) {
		} else {
			this.http
				.post(
					`http://localhost:4208/update-translate?key=${key}`,
					{},
					{
						headers: new HttpHeaders({
							"Cache-Control": "no-cache",
						}),
					},
				)
				.subscribe({
					next: (response: any) => {
						console.log("Translation update response:", response);
					},
					error: (err) => {
						console.error("Translation update error:", err);
					},
				});
		}
	}
}

export function createTranslateLoader(http: HttpClient) {
	return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

export const appConfig: ApplicationConfig = {
	providers: [
		provideAppInitializer(() => inject(SupabaseInitService).initialize()),
		provideRouter(
			CbRoutesUtils.angularRoutes,
			withComponentInputBinding(),
			withRouterConfig({ paramsInheritanceStrategy: "always" }),
			//withDebugTracing()
		),
		provideAnimations(),
		provideHttpClient(withFetch(), withInterceptorsFromDi()),
		provideClientHydration(
			withHttpTransferCacheOptions({
				includePostRequests: false,
			}),
		),
		importProvidersFrom(
			DndModule.forRoot({ backend: HTML5Backend }),
			DndSortableModule,
		),
		importProvidersFrom(
			TranslateModule.forRoot({
				defaultLanguage: getDefaultLanguage(),
				loader: {
					provide: TranslateLoader,
					useFactory: createTranslateLoader,
					deps: [HttpClient],
				},
				missingTranslationHandler: {
					provide: MissingTranslationHandler,
					useClass: MyMissingTranslationHandler,
				},
			}),
		),
		provideLottieOptions({ player: () => player }),
		provideCacheableAnimationLoader(),
		{ provide: LOCALE_ID, useFactory: () => getBrowserLocale() },
		{
			provide: IMAGE_CONFIG,
			useValue: {
				breakpoints: [16, 48, 96, 128, 384, 640, 750, 828, 1080, 1200, 1920],
			},
		},
		{
			provide: ErrorHandler,
			useValue: Sentry.createErrorHandler(),
		},
		// Instrumenta il routing Angular: senza TraceService browserTracingIntegration
		// cattura solo il pageload iniziale, non le transazioni di navigazione tra route.
		{
			provide: Sentry.TraceService,
			deps: [Router],
		},
		provideAppInitializer(() => {
			inject(Sentry.TraceService);
		}),
	],
};
